import React, { useState } from 'react';
import { alpha, Alert, Button, Grid, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { NoteData } from '../../../api';
import { formatSeconds } from '../utils/areasFormat';

type DayNotesAlertProps = {
  notes: NoteData[];
  timeFormat: string;
  onAddNote: () => void;
  onOpenNote: (note: NoteData) => void;
};

/**
 * The day's notes as a banner, in the same language as the unassigned-orders alert rather than a
 * floating badge. Notes never block availability, so this is information, not a warning.
 *
 * Nothing renders when there are no notes; adding one is already in the calendar toolbar.
 */
export const DayNotesAlert = ({ notes, timeFormat, onAddNote, onOpenNote }: DayNotesAlertProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // On a phone the action button leaves the preview ~130px, which clips it to a few characters.
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(false);

  if (notes.length === 0) {
    return null;
  }

  const sorted = [...notes].sort((a, b) => {
    if (a.all_day !== b.all_day) {
      return a.all_day ? -1 : 1;
    }
    return a.start_time - b.start_time;
  });

  const whenLabel = (note: NoteData) =>
    note.all_day ? t('calendar.areas_view.all_day') : formatSeconds(note.start_time, timeFormat);

  return (
    <Alert
      severity="info"
      icon={<NoteAltOutlinedIcon fontSize="inherit" />}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsExpanded(!isExpanded);
        }
      }}
      action={
        // The label costs about a third of a phone's width here, so it becomes a plus.
        isSmallScreen ? (
          <Tooltip title={t('calendar.areas_view.add_note')}>
            <IconButton
              size="small"
              aria-label={t('calendar.areas_view.add_note')}
              onClick={(event) => {
                event.stopPropagation();
                onAddNote();
              }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onAddNote();
            }}>
            {t('calendar.areas_view.add_note')}
          </Button>
        )
      }
      sx={{
        borderRadius: '8px',
        cursor: 'pointer',
        alignItems: 'center',
        // Without these the banner reads as a static notice rather than something to open.
        transition: 'background-color 120ms',
        '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.12) },
        '& .MuiAlert-message': { flex: 1, minWidth: 0 },
      }}>
      {isExpanded ? (
        <Grid container flexDirection="column" wrap="nowrap" gap={0.5}>
          <Grid container alignItems="center" gap={1} wrap="nowrap">
            <Typography sx={{ fontSize: '13px', fontWeight: 600, flex: 1 }}>
              {t('calendar.areas_view.notes_count', { count: notes.length })}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: '18px', flexShrink: 0, transform: 'rotate(180deg)' }} />
          </Grid>
          {sorted.map((note) => (
            <Grid
              key={note.id}
              container
              alignItems="baseline"
              gap={1}
              wrap="nowrap"
              onClick={(event) => {
                event.stopPropagation();
                onOpenNote(note);
              }}
              sx={{ '&:hover': { textDecoration: 'underline' } }}>
              <Typography dir="ltr" sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', minWidth: '64px' }}>
                {whenLabel(note)}
              </Typography>
              <Typography sx={{ fontSize: '13px' }}>{note.description}</Typography>
            </Grid>
          ))}
          <Typography sx={{ fontSize: '11px', opacity: 0.8 }}>{t('calendar.areas_view.note_no_block')}</Typography>
        </Grid>
      ) : (
        <Grid
          container
          flexDirection={isSmallScreen ? 'column' : 'row'}
          alignItems={isSmallScreen ? 'stretch' : 'center'}
          gap={isSmallScreen ? 0.25 : 1}
          wrap="nowrap">
          <Grid container alignItems="center" gap={1} wrap="nowrap">
            <Typography sx={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', flex: 1 }}>
              {t('calendar.areas_view.notes_count', { count: notes.length })}
            </Typography>
            {isSmallScreen && <ExpandMoreIcon sx={{ fontSize: '18px', flexShrink: 0 }} />}
          </Grid>
          <Typography
            sx={{
              fontSize: '13px',
              opacity: 0.9,
              overflow: 'hidden',
              minWidth: 0,
              // Given its own line on a phone, the preview gets two lines before it is cut.
              ...(isSmallScreen
                ? { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }
                : { textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }),
            }}>
            {sorted[0].description}
          </Typography>
          {!isSmallScreen && <ExpandMoreIcon sx={{ fontSize: '18px', flexShrink: 0 }} />}
        </Grid>
      )}
    </Alert>
  );
};
